import { routes } from '@/routes';

/**
 * Safe referral paths for Portal navigation (no open redirects).
 * Canonical Portal paths and legacy Dashboard/Partner Portal paths are accepted.
 */

const PREFIX = routes.partnerPortal;
const LEGACY_PREFIXES = ['/dashboard', '/partner-portal'] as const;
const KIT_AUTHORIZE_PREFIX = routes.kitAuthorize;
const KIT_EDIT_PREFIX = routes.kitEdit;
const RETIRED_PARTNER_PORTAL_VIEWS = new Set([
  'new-project',
  'drafts',
  'create',
  'editor',
  'email',
  'embed',
  'embed-editor',
  'style',
]);

function normalizeRetiredPartnerPortalView(pathWithQuery: string): string {
  const legacyPrefix = LEGACY_PREFIXES.find((prefix) => matchesPortalPrefix(pathWithQuery, prefix));
  const canonicalPathWithQuery = legacyPrefix
    ? `${PREFIX}${pathWithQuery.slice(legacyPrefix.length)}`
    : pathWithQuery;
  const queryIndex = canonicalPathWithQuery.indexOf('?');
  if (queryIndex < 0) return canonicalPathWithQuery;

  const path = canonicalPathWithQuery.slice(0, queryIndex);
  const searchParams = new URLSearchParams(canonicalPathWithQuery.slice(queryIndex + 1));
  const requestedView = searchParams.get('view');
  if (!requestedView || !RETIRED_PARTNER_PORTAL_VIEWS.has(requestedView)) {
    return canonicalPathWithQuery;
  }

  searchParams.set('view', 'library');
  return `${path}?${searchParams.toString()}`;
}

function matchesPortalPrefix(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}?`) || path.startsWith(`${prefix}/`);
}

function matchesExactReturnPath(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}?`);
}

export function isValidPartnerPortalReturnPath(path: string): boolean {
  const trimmed = path.trim();
  if (trimmed.includes('://')) return false;
  if (trimmed.includes('//')) return false;
  if (matchesExactReturnPath(trimmed, KIT_AUTHORIZE_PREFIX)) return true;
  if (matchesExactReturnPath(trimmed, KIT_EDIT_PREFIX)) return true;
  return [PREFIX, ...LEGACY_PREFIXES].some((prefix) => matchesPortalPrefix(trimmed, prefix));
}

/**
 * Decode and validate `returnTo` from URL search params (percent-encoded path + query).
 */
export function parsePartnerPortalReturnTo(raw: string | null | undefined): string | null {
  if (raw == null || raw === '') return null;
  try {
    const decoded = decodeURIComponent(raw.trim());
    return isValidPartnerPortalReturnPath(decoded)
      ? normalizeRetiredPartnerPortalView(decoded)
      : null;
  } catch {
    return null;
  }
}

export function encodePartnerPortalReturnTo(pathWithQuery: string): string {
  return encodeURIComponent(pathWithQuery);
}

/** Default after upgrade when referral is missing or invalid. */
export const DEFAULT_PARTNER_UPGRADE_RETURN_PATH = `${PREFIX}?view=library`;

/** Portal preferences view — unified subscription management surface. */
export const DEFAULT_PARTNER_PREFERENCES_PATH = `${PREFIX}?view=preferences`;

/** Default return path for subscription-management flows (preferences). */
export const DEFAULT_PARTNER_MANAGE_SUBSCRIPTION_PATH = DEFAULT_PARTNER_PREFERENCES_PATH;

/** Query param that opens the sign-in modal on the Portal gate page. */
export const OPEN_SIGN_IN_PARAM = 'signIn';

export function buildPartnerPortalSignInPath(): string {
  const qs = new URLSearchParams();
  qs.set(OPEN_SIGN_IN_PARAM, '1');
  return `${PREFIX}?${qs.toString()}`;
}

/** Sign-in path that opens the Affiliate view in the Portal after authentication. */
export function buildAffiliatePortalSignInPath(): string {
  const qs = new URLSearchParams();
  qs.set('view', 'affiliate');
  qs.set(OPEN_SIGN_IN_PARAM, '1');
  return `${PREFIX}?${qs.toString()}`;
}

/** Query param that opens the Manage subscription modal on Preferences. */
export const OPEN_MANAGE_SUBSCRIPTION_PARAM = 'openManageSubscription';

/** URL param that triggers the post-upgrade celebration modal. */
export const UPGRADE_CELEBRATION_PARAM = 'upgraded';
export const UPGRADE_CELEBRATION_VALUE = 'business';

/**
 * Append the celebration query param to a validated partner-portal path.
 */
export function appendUpgradeCelebrationParam(pathWithQuery: string): string {
  const qIndex = pathWithQuery.indexOf('?');
  const path = qIndex >= 0 ? pathWithQuery.slice(0, qIndex) : pathWithQuery;
  const qs = new URLSearchParams(qIndex >= 0 ? pathWithQuery.slice(qIndex + 1) : '');
  qs.set(UPGRADE_CELEBRATION_PARAM, UPGRADE_CELEBRATION_VALUE);
  const query = qs.toString();
  return query ? `${path}?${query}` : path;
}

/** True when the URL indicates a fresh Business upgrade redirect. */
export function parseUpgradeCelebration(searchParams: URLSearchParams): boolean {
  return searchParams.get(UPGRADE_CELEBRATION_PARAM) === UPGRADE_CELEBRATION_VALUE;
}

/**
 * Search params for post-upgrade navigation: return-path view params + celebration flag.
 * Clears `returnTo` so the upgrade referral does not linger in the URL.
 */
export function buildPostUpgradeSearchParams(returnPath: string): URLSearchParams {
  const normalizedReturnPath = normalizeRetiredPartnerPortalView(returnPath);
  const qIndex = normalizedReturnPath.indexOf('?');
  const qs = new URLSearchParams(qIndex >= 0 ? normalizedReturnPath.slice(qIndex + 1) : '');
  qs.set(UPGRADE_CELEBRATION_PARAM, UPGRADE_CELEBRATION_VALUE);
  qs.delete('returnTo');
  return qs;
}
